import { create } from "zustand";
import {
  fetchTableTemplates,
  fetchTableTemplateById,
  createTableTemplate,
  updateTableTemplate,
  deleteTableTemplate,
  lockTableTemplate,
  unlockTableTemplate,
  dtoToSchema,
  stringifyTableTemplateSchema,
  type TableTemplateRecord,
  type TableTemplateLockState,
  type CreateTableTemplateDto,
  type UpdateTableTemplateDto,
} from "../apiServices/tableTemplatesApi";

import type { TableTemplate, TableColumn } from "../models/ManualAuthoring";

type TableTemplateStoreState = {
  oem: string | null;

  templates: TableTemplateRecord[];
  selectedTemplate: TableTemplateRecord | null;

  editorSchema: TableTemplate | null;

  lockState: TableTemplateLockState | null;

  selectedColumnId: string | null;

  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
};

type TableTemplateStoreActions = {
  setOEM: (oem: string) => void;

  loadTemplates: () => Promise<void>;

  selectTemplate: (tableId: number) => Promise<void>;

  createTemplate: (
    templateName: string,
    displayTitle: string,
    schema: TableTemplate
  ) => Promise<void>;

  saveTemplate: () => Promise<void>;

  deleteTemplate: (tableId: number) => Promise<void>;

  lockTemplate: (tableId: number) => Promise<void>;

  unlockTemplate: (tableId: number) => Promise<void>;

  updateSchema: (schema: TableTemplate) => void;

  selectColumn: (columnId: string | null) => void;

  addRootColumn: () => void;

  addChildColumn: (parentId: string) => void;

  updateColumn: (columnId: string, updates: Partial<TableColumn>) => void;

  deleteColumn: (columnId: string) => void;

  startNewTemplate: () => void;

  clearSelection: () => void;
};

type TableTemplateStore = TableTemplateStoreState & TableTemplateStoreActions;

function generateColumn(): TableColumn {
  return {
    id: crypto.randomUUID(),
    header: "New Column",
    width: 120,
    columns: [],
  };
}

function getDefaultNewTemplate(): TableTemplate {
  return {
    templateName: "new_table_template",
    displayTitle: "New Table Template",
    description: "",
    defaultRows: 10,
    width: "100%",
    alignment: "left",
    headerRepeat: true,
    keepTogether: false,
    schemaVersion: 1,
    columns: [],
    rows: [],
  };
}

function updateColumnTree(
  columns: TableColumn[],
  columnId: string,
  updates: Partial<TableColumn>
): TableColumn[] {
  return columns.map((col) => {
    if (col.id === columnId) {
      return { ...col, ...updates };
    }

    if (col.columns?.length) {
      return {
        ...col,
        columns: updateColumnTree(col.columns, columnId, updates),
      };
    }

    return col;
  });
}

function deleteColumnTree(columns: TableColumn[], columnId: string): TableColumn[] {
  return columns
    .filter((col) => col.id !== columnId)
    .map((col) => ({
      ...col,
      columns: col.columns ? deleteColumnTree(col.columns, columnId) : [],
    }));
}

function addChildColumnTree(
  columns: TableColumn[],
  parentId: string
): TableColumn[] {
  return columns.map((col) => {
    if (col.id === parentId) {
      return {
        ...col,
        columns: [...(col.columns ?? []), generateColumn()],
      };
    }

    if (col.columns?.length) {
      return {
        ...col,
        columns: addChildColumnTree(col.columns, parentId),
      };
    }

    return col;
  });
}

export const useTableTemplateStore = create<TableTemplateStore>((set, get) => ({
  oem: null,

  templates: [],
  selectedTemplate: null,

  editorSchema: null,

  lockState: null,

  selectedColumnId: null,

  isLoading: false,
  isSaving: false,
  error: null,

  setOEM: (oem) => set({ oem }),

  loadTemplates: async () => {
    const { oem } = get();
    if (!oem) return;

    set({ isLoading: true, error: null });

    try {
      const templates = await fetchTableTemplates(oem);

      set({
        templates,
        isLoading: false,
      });
    } catch (err: any) {
      set({
        error: err.message,
        isLoading: false,
      });
    }
  },

  selectTemplate: async (tableId) => {
    const { oem } = get();
    if (!oem) return;

    set({ isLoading: true, error: null });

    try {
      const template = await fetchTableTemplateById(oem, tableId);

      const schema = dtoToSchema(template);

      set({
        selectedTemplate: template,
        editorSchema: schema,
        selectedColumnId: null,
        lockState: {
          tableId: template.tableId,
          isLocked: template.isLocked,
          lockedBy: template.lockedBy,
          lockedAt: template.lockedAt,
        },
        isLoading: false,
      });
    } catch (err: any) {
      set({
        error: err.message,
        isLoading: false,
      });
    }
  },

  createTemplate: async (templateName, displayTitle, schema) => {
    const { oem } = get();
    if (!oem) return;

    set({ isSaving: true, error: null });

    try {
      const payload: CreateTableTemplateDto = {
        templateName,
        displayTitle,
        tableData: stringifyTableTemplateSchema(schema),
        schemaVersion: schema.schemaVersion,
        width: schema.width,
        alignment: schema.alignment,
        headerRepeat: schema.headerRepeat,
        keepTogether: schema.keepTogether,
      };

      const newTemplate = await createTableTemplate(oem, payload);

      set((state) => ({
        templates: [...state.templates, newTemplate],
        selectedTemplate: newTemplate,
        editorSchema: schema,
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message,
        isSaving: false,
      });
    }
  },

  saveTemplate: async () => {
    const { oem, selectedTemplate, editorSchema } = get();

    if (!oem || !selectedTemplate || !editorSchema) return;

    set({ isSaving: true, error: null });

    try {
      const templateName =
        editorSchema.templateName?.trim() || selectedTemplate.templateName;
      const displayTitle =
        editorSchema.displayTitle?.trim() || selectedTemplate.displayTitle;

      const payload: UpdateTableTemplateDto = {
        tableId: selectedTemplate.tableId,
        templateName,
        displayTitle,
        tableData: stringifyTableTemplateSchema(editorSchema),
        schemaVersion: editorSchema.schemaVersion,
        width: editorSchema.width,
        alignment: editorSchema.alignment,
        headerRepeat: editorSchema.headerRepeat,
        keepTogether: editorSchema.keepTogether,
        rowVersion: selectedTemplate.rowVersion!,
      };

      const updated = await updateTableTemplate(oem, payload);

      set((state) => ({
        templates: state.templates.map((t) =>
          t.tableId === updated.tableId ? updated : t
        ),
        selectedTemplate: updated,
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message,
        isSaving: false,
      });
    }
  },

  deleteTemplate: async (tableId) => {
    const { oem } = get();
    if (!oem) return;

    set({ isSaving: true });

    try {
      await deleteTableTemplate(oem, tableId);

      set((state) => ({
        templates: state.templates.filter((t) => t.tableId !== tableId),
        selectedTemplate:
          state.selectedTemplate?.tableId === tableId
            ? null
            : state.selectedTemplate,
        editorSchema:
          state.selectedTemplate?.tableId === tableId
            ? null
            : state.editorSchema,
        isSaving: false,
      }));
    } catch (err: any) {
      set({
        error: err.message,
        isSaving: false,
      });
    }
  },

  lockTemplate: async (tableId) => {
    const { oem } = get();
    if (!oem) return;

    const lockState = await lockTableTemplate(oem, tableId);

    set({ lockState });
  },

  unlockTemplate: async (tableId) => {
    const { oem } = get();
    if (!oem) return;

    const lockState = await unlockTableTemplate(oem, tableId);

    set({ lockState });
  },

  selectColumn: (columnId) => {
    set({ selectedColumnId: columnId });
  },

  addRootColumn: () => {
    const { editorSchema } = get();
    if (!editorSchema) return;

    const newColumn = generateColumn();

    set({
      editorSchema: {
        ...editorSchema,
        columns: [...editorSchema.columns, newColumn],
      },
    });
  },

  addChildColumn: (parentId) => {
    const { editorSchema } = get();
    if (!editorSchema) return;

    set({
      editorSchema: {
        ...editorSchema,
        columns: addChildColumnTree(editorSchema.columns, parentId),
      },
    });
  },

  updateColumn: (columnId, updates) => {
    const { editorSchema } = get();
    if (!editorSchema) return;

    set({
      editorSchema: {
        ...editorSchema,
        columns: updateColumnTree(editorSchema.columns, columnId, updates),
      },
    });
  },

  deleteColumn: (columnId) => {
    const { editorSchema } = get();
    if (!editorSchema) return;

    set({
      editorSchema: {
        ...editorSchema,
        columns: deleteColumnTree(editorSchema.columns, columnId),
      },
    });
  },

  startNewTemplate: () => {
    set({
      selectedTemplate: null,
      editorSchema: getDefaultNewTemplate(),
      selectedColumnId: null,
      lockState: null,
      error: null,
    });
  },

  updateSchema: (schema) => set({ editorSchema: schema }),

  clearSelection: () =>
    set({
      selectedTemplate: null,
      editorSchema: null,
      lockState: null,
      selectedColumnId: null,
    }),
}));
